import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import ZipmapRepository from "../../repository/ZipmapRepository";
import EditButton from "../../shared/components/dashboard/EditButton";
import { LazyLoadImage } from "react-lazy-load-image-component";
import CoorayFooter from "../../shared/components/footer/CoorayFooter";
import EditorModal from "../../shared/components/editor/EditorModal";
import EditInputModal from "./EditInputModal";
import DashboardLiveStatusModal from "./DashboardLiveStatusModal";
import data from '@emoji-mart/data'
import Picker from '@emoji-mart/react'
import DashboardDefaultViewModal from "./DashboardDefaultViewModal";
import { generateQRCodeDownloadUrl, generateQRCodeSvg } from "../../shared/utils/QRUtils";
import DownloadButton from "../../shared/components/dashboard/DownloadButton";
import DeleteButton from "../../shared/components/dashboard/DeleteButton";
import { downsizeImage } from "../../shared/utils/ImageResizeUtils";
import EditRichTextModal from "./EditRichTextModal";
import { TiptapControlEnablement } from "../../shared/components/editor/Tiptap";
import DashboardSortStatusModal from "./DashboardSortStatusModal";
import { HexColorInput, HexColorPicker } from "react-colorful";
import ColorSelectorModal from "../../shared/components/colorselector/ColorSelectorModal";

const IMAGE_MAX_DIMENSION = 1440

export default function DashboardPageEditMap({ 
    analytics, 
    viewingMap, 
    setFullScreenLoadingText,
    onMapUpdated,
    onMapDeleted
 }) {

    const navigate = useNavigate()
    const [loadingText, setLoadingText] = useState(null)
    const [currentMap, setCurrentMap] = useState(viewingMap)
    const currentMapRef = useRef(currentMap)
    const [isEditorModalOpen, setIsEditorModalOpen] = useState(false)
    const [editingEditorKey, setEditingEditorKey] = useState(null)
    const [initialEditorValue, setInitialEditorValue] = useState(null)

    const [isEditInputModalOpen, setIsEditInputModalOpen] = useState(false)
    const [editingInputKey, setEditingInputKey] = useState(null)
    const [initialInputValue, setInitialInputValue] = useState(null)
    const [inputLabel, setInputLabel] = useState(null)
    const [inputPlaceholder, setInputPlaceholder] = useState(null)
    const [inputPretext, setInputPretext] = useState(null)
    const [editSlugWarningMessageParameters, setEditSlugWarningMessageParameters] = useState(null)


    const [isEditRichTextModalOpen, setIsEditRichTextModalOpen] = useState(false)
    const [editingRichTextKey, setEditingRichTextKey] = useState(null)
    const [initialRichTextValue, setInitialRichTextValue] = useState(null)
    const [richTextLabel, setRichTextLabel] = useState(null)

    const [isDashboardLiveStatusModalOpen, setIsDashboardLiveStatusModalOpen] = useState(false)
    const [isDashboardSortStatusModalOpen, setIsDashboardSortStatusModalOpen] = useState(false)
    const [isDashboardDefaultViewModalOpen, setIsDashboardDefaultViewModalOpen] = useState(false)
    
    const [toastMessage, setToastMessage] = useState(null)
    const [qrCodeSvg, setQrCodeSvg] = useState(null)
    const [qrCodeDownloadUrl, setQrCodeDownloadUrl] = useState(null)

    const [primaryColor, setPrimaryColor] = useState(viewingMap.primaryColor);
    const [isColorSelectorModalOpen, setIsColorSelectorModalOpen] = useState(false)


    useEffect(() => {
        currentMapRef.current = currentMap
    }, [currentMap])

    useEffect(() => {
        if (toastMessage) {
            setTimeout(() => {
                setToastMessage(null)
            }, 3000)
        }
    }, [toastMessage])

    useEffect(() => {
        setCurrentMap(viewingMap)
        setPrimaryColor(viewingMap.primaryColor)
    }, [viewingMap])

    useEffect(() => {

        async function generateQRCode(slug) {
            const svg = await generateQRCodeSvg(slug)
            setQrCodeSvg(svg)
        }

        async function generateQRCodeUrl(slug) {
            const downloadUrl = await generateQRCodeDownloadUrl(slug)
            setQrCodeDownloadUrl(downloadUrl)
        }

        if (currentMap.slug) {
            generateQRCode(`https://zipmap.io/${currentMap.slug}`)
            generateQRCodeUrl(`https://zipmap.io/${currentMap.slug}`)
        }

    }, [currentMap.slug])

    async function onFileInputChange(inputEvent, type) {
  
        if (inputEvent.target.files) {
          setFullScreenLoadingText("Processing image ...")

          let imageFile = inputEvent.target.files[0];
          const resizedBlob = await downsizeImage(imageFile);
          console.log('Original size:', (imageFile.size / 1024).toFixed(2), 'KB');
          console.log('Resized size:', (resizedBlob.size / 1024).toFixed(2), 'KB');

          let file = new File([resizedBlob], "fileName.jpg", { type: "image/jpeg" })
          if (type === "cover") {
              editMapCoverPhoto(file)
          } else if (type === "logo") {
              editMapLogoImage(file)
          } else if (type === "darkLogo") {
              editMapDarkLogoImage(file)
          }
      }
  
    }

    function onEditTitleClick() {
        setEditingRichTextKey("name")
        setInitialRichTextValue(currentMap.displayName)
        setRichTextLabel("Map name")
        setIsEditRichTextModalOpen(true)
    }

    function onEditLiveStatusClick() {
        setIsDashboardLiveStatusModalOpen(true)
    }

    function onEditDefaultViewClick() {
        setIsDashboardDefaultViewModalOpen(true)
    }

    function onEditPrimaryColorClick() {
        setIsColorSelectorModalOpen(true)
    }

    function onEditSlugClick() {
        setEditingInputKey("slug")
        setInitialInputValue(currentMapRef.current.slug)
        setInputLabel("Map URL")
        setInputPlaceholder("Enter map URL here ...")
        setInputPretext("zipmap.io/")
        setIsEditInputModalOpen(true)
    }

    function onEditDefaultMarkerClick() {
        document.getElementById("pick-emoji-modal").showModal()
    }

    function onEditInputModalDismiss() {
        setIsEditInputModalOpen(false)
        setEditingInputKey(null)
        setInitialInputValue(null)
        setInputLabel(null)
        setInputPretext(null)
        setInputPlaceholder(null)
    }
    
    function onEditDescriptionClick() {
        setEditingEditorKey("description")
        setInitialEditorValue(currentMapRef.current.description)
        setIsEditorModalOpen(true)
    }

    function onEditorModalDismiss() {
        setIsEditorModalOpen(false)
    }

    async function editMapCoverPhoto(fileToBeUploaded) {
        // const imagefile = document.querySelector('#upload-image-input')
        // const fileToBeUploaded = imagefile.files[0]
        console.log(`image file ${fileToBeUploaded}`)
        setFullScreenLoadingText("Uploading image ...")
        const zipmapRepository = new  ZipmapRepository()
        const response = await zipmapRepository.uploadCoverPhoto(
            currentMapRef.current.slug, fileToBeUploaded
        )
        const updatedMap = {
            ...currentMapRef.current,
            coverPhoto: response.map.coverPhoto
        }
        setCurrentMap(updatedMap)
        onMapUpdated(updatedMap)
        setFullScreenLoadingText(null)

        setToastMessage({
            message: `🎉 Map cover photo updated successfully`,
            type: "success"
        })
    }

    async function editMapLogoImage(fileToBeUploaded) {
        // const imagefile = document.querySelector('#upload-image-input')
        // const fileToBeUploaded = imagefile.files[0]
        console.log(`image file ${fileToBeUploaded}`)
        setFullScreenLoadingText("Uploading image ...")
        const zipmapRepository = new  ZipmapRepository()
        const response = await zipmapRepository.uploadLogoImage(
            currentMapRef.current.slug, fileToBeUploaded
        )
        const updatedMap = {
            ...currentMapRef.current,
            logo: response.map.logo
        }
        setCurrentMap(updatedMap)
        onMapUpdated(updatedMap)
        setFullScreenLoadingText(null)

        setToastMessage({
            message: `🎉 Map logo updated successfully`,
            type: "success"
        })
    }

    async function editMapDarkLogoImage(fileToBeUploaded) {
        // const imagefile = document.querySelector('#upload-image-input')
        // const fileToBeUploaded = imagefile.files[0]
        console.log(`image file ${fileToBeUploaded}`)
        setFullScreenLoadingText("Uploading image ...")
        const zipmapRepository = new  ZipmapRepository()
        const response = await zipmapRepository.uploadDarkLogoImage(
            currentMapRef.current.slug, fileToBeUploaded
        )
        const updatedMap = {
            ...currentMapRef.current,
            darkLogo: response.map.darkLogo
        }
        setCurrentMap(updatedMap)
        onMapUpdated(updatedMap)
        setFullScreenLoadingText(null)

        setToastMessage({
            message: `🎉 Map logo updated successfully`,
            type: "success"
        })
    }

    async function editMapDefaultView(type) {
        setFullScreenLoadingText("Saving ...")
        const zipmapRepository = new ZipmapRepository()
        if (type === "LIST") {
            await zipmapRepository.editMapDefaultViewToList(currentMapRef.current.slug)
        } else if (type === "MAP") {
            await zipmapRepository.editMapDefaultViewToMap(currentMapRef.current.slug)
        } else {
            setFullScreenLoadingText(null)
            return
        }
        const updatedMap = {
            ...currentMapRef.current,
            defaultView: type
        }
        setCurrentMap(updatedMap)
        setFullScreenLoadingText(null)
        onMapUpdated(updatedMap)

        setToastMessage({
            message: `🎉 Map default view updated to ${type}`,
            type: "success"
        })
    }
    
    async function editMapDescription(html) {
        setFullScreenLoadingText("Saving ...")
        const zipmapRepository = new ZipmapRepository()
        await zipmapRepository.editMapDescription(currentMapRef.current.slug, html)
        const updatedMap = {
            ...currentMapRef.current,
            description: html
        }
        setCurrentMap(updatedMap)
        setFullScreenLoadingText(null)
        onMapUpdated(updatedMap)

        setToastMessage({
            message: `🎉 Map description updated successfully`,
            type: "success"
        })
    }

    async function editMapName(name) {
        setFullScreenLoadingText("Saving ...")
        const zipmapRepository = new ZipmapRepository()
        const response = await zipmapRepository.editMapName(currentMapRef.current.slug, name)
        const updatedMap = {
            ...currentMapRef.current,
            displayName: response.map.displayName,
            name: response.map.name
        }
        setCurrentMap(updatedMap)
        setFullScreenLoadingText(null)
        onMapUpdated(updatedMap)
        setToastMessage({
            message: `🎉 Map name updated successfully`,
            type: "success"
        })
    }

    async function editMapPrimaryColor(primaryColor) {
        setFullScreenLoadingText("Saving ...")
        const zipmapRepository = new ZipmapRepository()
        await zipmapRepository.editMapPrimaryColor(currentMapRef.current.slug, primaryColor)
        const updatedMap = {
            ...currentMapRef.current,
            primaryColor: primaryColor
        }
        setCurrentMap(updatedMap)
        setFullScreenLoadingText(null)
        onMapUpdated(updatedMap)
        setToastMessage({
            message: `🎉 Map primary color updated successfully`,
            type: "success"
        })
    }


    async function editMapSlug(slug) {
        setFullScreenLoadingText("Saving ...")
        const zipmapRepository = new ZipmapRepository()
        await zipmapRepository.editMapSlug(currentMapRef.current.slug, slug)
        const updatedMap = {
            ...currentMap,
            slug: slug
        }
        setCurrentMap(updatedMap)
        setFullScreenLoadingText(null)
        onMapUpdated(updatedMap)

        setToastMessage({
            message: `🎉 Map URL updated successfully`,
            type: "success"
        })
    }

    async function editMapLiveStatus(isLive) {
        setFullScreenLoadingText("Saving ...")
        const zipmapRepository = new ZipmapRepository()
        await zipmapRepository.editMapLiveStatus(currentMapRef.current.slug, isLive)
        const updatedMap = {
            ...currentMapRef.current,
            isLive: isLive
        }
        setCurrentMap(updatedMap)
        setFullScreenLoadingText(null)
        onMapUpdated(updatedMap)

        setToastMessage({
            message: `Map live status updated to ${isLive ? "🟢 Live" : "🔴 Paused"}`,
            type: "info"
        })
    }

    async function editMapIsSortAllowed(isSortAllowed) {
        setFullScreenLoadingText("Saving ...")
        const zipmapRepository = new ZipmapRepository()
        await zipmapRepository.editMapIsSortAllowed(currentMapRef.current.slug, isSortAllowed)
        const updatedMap = {
            ...currentMapRef.current,
            isSortAllowed: isSortAllowed
        }
        setCurrentMap(updatedMap)
        setFullScreenLoadingText(null)
        onMapUpdated(updatedMap)

        setToastMessage({
            message: `Map sort button updated to ${isSortAllowed ? "🟢 Visible" : "🔴 Hidden"}`,
            type: "info"
        })
    }

    async function editMapDefaultMarker(marker) {
        setFullScreenLoadingText("Saving ...")
        console.log(JSON.stringify(currentMap))
        const zipmapRepository = new ZipmapRepository()
        await zipmapRepository.editMapDefaultMarker(currentMapRef.current.slug, marker)
        const updatedMap = {
            ...currentMapRef.current,
            defaultMarker: marker
        }
        console.log("updatedMap")
        console.log(JSON.stringify(updatedMap))
        setCurrentMap(updatedMap)
        setFullScreenLoadingText(null)
        onMapUpdated(updatedMap)

        setToastMessage({
            message: `🎉 Map default marker updated to ${marker}`,
            type: "info"
        })
    }

    function onEditorSaveClick(html, editingKey) {
        console.log(html)
        switch (editingKey) {
            case "description":
                editMapDescription(html)
                break
            default:
                break
        }
        setIsEditorModalOpen(false)
        setEditingEditorKey(null)
        setInitialEditorValue(null)
    }

    function onEditInputSaveClick(inputValue, editingKey) {
        console.log(inputValue)
        switch (editingKey) {
            case "name":
                editMapName(inputValue)
                setIsEditInputModalOpen(false)
                setEditingInputKey(null)
                setInitialInputValue(null)
                setInputLabel(null)
                setInputPlaceholder(null)
                setInputPretext(null)
                break
            case "slug":
                setEditSlugWarningMessageParameters({ oldSlug: currentMapRef.current.slug, newSlug: inputValue })
                document.getElementById("edit-slug-modal").showModal()
                setIsEditInputModalOpen(false)
                setEditingInputKey(null)
                setInitialInputValue(null)
                setInputLabel(null)
                setInputPlaceholder(null)
                setInputPretext(null)
                break
            case "delete":
                if (inputValue === "delete") {
                    confirmDeleteMap()
                    setIsEditInputModalOpen(false)
                    setEditingInputKey(null)
                    setInitialInputValue(null)
                    setInputLabel(null)
                    setInputPlaceholder(null)
                    setInputPretext(null)
                }
                break
            default:
                break
        }
    }

    function onDashboardLiveStatusSaveClick(isLive) {
        setIsDashboardLiveStatusModalOpen(false)
        editMapLiveStatus(isLive)
    }

    function onDashboardSortStatusSaveClick(isSortAllowed) {
        setIsDashboardSortStatusModalOpen(false)
        editMapIsSortAllowed(isSortAllowed)
    }

    function onDashboardDefaultViewSaveClick(viewType) {
        setIsDashboardDefaultViewModalOpen(false)
        editMapDefaultView(viewType)
    }

    function onEmojiSelected(emoji) {
        console.log(`emojiSelected ${JSON.stringify(currentMap)}`)
        editMapDefaultMarker(emoji)
        document.getElementById("pick-emoji-modal").close()
    }

    function onDeleteMapClick() {
        setEditingInputKey("delete")
        setInitialInputValue("")
        setInputLabel("Type \"delete\" in the box below to delete the map")
        setInputPlaceholder("Type \"delete\" to confirm ...")
        setInputPretext(null)
        setIsEditInputModalOpen(true)
    }

    function onCopyUrlClick() {
        navigator.clipboard.writeText(`https://zipmap.io/${currentMap.slug}`)
        setToastMessage({
            message: `🎉 URL copied to clipboard`,
            type: "info"
        })
    }

    async function confirmDeleteMap() {
        setFullScreenLoadingText("Deleting ...")
        const zipmapRepository = new ZipmapRepository()
        await zipmapRepository.deleteMap(currentMapRef.current.slug)
        setToastMessage({
            message: `🎉 Map deleted successfully`,
            type: "info"
        })
        setFullScreenLoadingText(null)
        onMapDeleted()
    }

    function onEditRichTextSaveClick(html, editingKey) {
        console.log(`onEditRichTextSaveClick ${editingKey} ${html}`)
        switch (editingKey) {
            case "name":
                editMapName(html)
                setIsEditRichTextModalOpen(false)
                setEditingRichTextKey(null)
                setInitialRichTextValue(null)
                setRichTextLabel(null)
                break
            default:
                break
        }
    }

    function onEditIsSortAllowedClick() {
        setIsDashboardSortStatusModalOpen(true)
    }

    function onColorSaveClick(color) {
        setPrimaryColor(color)
        setIsColorSelectorModalOpen(false)
        editMapPrimaryColor(color)
    }

    return (
        <div className="h-full w-full flex flex-col flex-1 py-4 bg-base-200">
            <div className="px-4 md:px-8 text-left text-md text-base-content/70 pb-1">
                Status
            </div>
            <div className="flex flex-row items-center px-4 md:px-8">
                {
                    currentMap.isLive ? <div className="font-bold text-left text-lg pr-4">
                    <span className="animate-pulse pr-2">🟢</span> Live
                    </div> : <div className="font-bold text-left text-lg pr-4">
                        <span className="pr-2">🔴</span> Paused
                    </div>
                }
                <div className="shrink-0 "><EditButton onEditClick={onEditLiveStatusClick} /></div>
            </div>
            <div className="px-4 md:px-8 text-left text-md text-base-content/70 pb-1 pt-8">
                Map name
            </div>
            <div className="flex flex-row items-center px-4 md:px-8">
                <div className="font-bold font-titan text-left text-4xl pr-4" dangerouslySetInnerHTML={{ __html: currentMap.displayName }}>
                </div>
                <div className="shrink-0 "><EditButton onEditClick={onEditTitleClick} /></div>
            </div>

            <div className="px-4 md:px-8 text-left text-md text-base-content/70 pt-8 pb-1">
                Map url
            </div>
            <div className="flex flex-col md:flex-row md:items-center px-4 md:px-8 gap-2">
                <div className="font-bold text-left text-lg pr-4">
                    <span className="text-base-content/70">zipmap.io/</span>{currentMap.slug}
                </div>
                <div className="shrink-0 flex flex-row items-center gap-2">
                    <EditButton onEditClick={onEditSlugClick} />
                    <button className="btn btn-sm btn-soft btn-accent normal-case" onClick={onCopyUrlClick}>
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path className="fill-accent-content" d="M15.24 2H11.3458C9.58159 1.99999 8.18418 1.99997 7.09054 2.1476C5.96501 2.29953 5.05402 2.61964 4.33559 3.34096C3.61717 4.06227 3.29833 4.97692 3.14701 6.10697C2.99997 7.205 2.99999 8.60802 3 10.3793V16.2169C3 17.725 3.91995 19.0174 5.22717 19.5592C5.15989 18.6498 5.15994 17.3737 5.16 16.312L5.16 11.3976L5.16 11.3024C5.15993 10.0207 5.15986 8.91644 5.27828 8.03211C5.40519 7.08438 5.69139 6.17592 6.4253 5.43906C7.15921 4.70219 8.06404 4.41485 9.00798 4.28743C9.88877 4.16854 10.9887 4.1686 12.2652 4.16867L12.36 4.16868H15.24L15.3348 4.16867C16.6113 4.1686 17.7088 4.16854 18.5896 4.28743C18.0627 2.94779 16.7616 2 15.24 2Z"/>
                    <path className="fill-accent-content" d="M6.6001 11.3974C6.6001 8.67119 6.6001 7.3081 7.44363 6.46118C8.28716 5.61426 9.64481 5.61426 12.3601 5.61426H15.2401C17.9554 5.61426 19.313 5.61426 20.1566 6.46118C21.0001 7.3081 21.0001 8.6712 21.0001 11.3974V16.2167C21.0001 18.9429 21.0001 20.306 20.1566 21.1529C19.313 21.9998 17.9554 21.9998 15.2401 21.9998H12.3601C9.64481 21.9998 8.28716 21.9998 7.44363 21.1529C6.6001 20.306 6.6001 18.9429 6.6001 16.2167V11.3974Z"/>
                    </svg>
                        Copy URL
                    </button>
                </div>
            </div>

            <div className="px-4 md:px-8 text-left text-md text-base-content/70 pt-8 pb-1">
                QR Code
            </div>
            {
                qrCodeSvg && <div className="flex flex-row items-center px-4 md:px-8 pt-2">
                    <div dangerouslySetInnerHTML={{ __html: qrCodeSvg }}></div>
                </div>
            }
            {
                qrCodeDownloadUrl && <div className="px-4 md:px-8 text-left text-sm text-base-content/50 pt-2">
                    <a href={qrCodeDownloadUrl} download={`${currentMap.slug}-qr.png`}>
                        <DownloadButton onDownloadClick={() => {}} />
                    </a>
                </div>
            }

            <div className="px-4 md:px-8 text-left text-md text-base-content/70 pt-8 pb-1">
                Map default marker
            </div>
            <div className="flex flex-row items-center px-4 md:px-8">
                <div className="font-bold text-left text-4xl pr-4">
                    {currentMap.defaultMarker}
                </div>
                <div className="shrink-0 "><EditButton onEditClick={onEditDefaultMarkerClick} /></div>
            </div>


            <div className="px-4 md:px-8 text-left text-md text-base-content/70 pt-8 pb-1">
                Map cover picture
            </div>
            <div className="flex flex-col items px-4 md:px-8">
                <input id="upload-add-map-cover-photo-input" type="file" accept="image/png, image/jpeg" className="file-input file-input-bordered w-full max-w-xs hidden" onChange={(inputEvent) => { onFileInputChange(inputEvent, "cover") }}/>

                {
                    currentMap.coverPhoto && <LazyLoadImage 
                    className='max-w-[500px] object-containe rounded-md'
                    src={currentMap.coverPhoto}
                    />
                }
                <div className="shrink-0 pt-2"><EditButton onEditClick={() => {
                    document.getElementById('upload-add-map-cover-photo-input').click();
                }} /></div>
            </div>

            <div className="px-4 md:px-8 text-left text-md text-base-content/70 pt-8 pb-1">
                Map logo
            </div>
            <div className="flex flex-row items-center px-4 md:px-8 gap-4">
                <div className="flex flex-col">
                    <div className="font-bold text-left text-lg pr-4">
                        Light mode
                    </div>
                    {
                        currentMap.logo && <LazyLoadImage 
                        className='max-w-[200px] max-h-[200px] object-contain rounded-md'
                        src={currentMap.logo}
                        />
                    }

                    <input id="upload-add-map-logo-photo-input" type="file" accept="image/png, image/jpeg" className="file-input file-input-bordered w-full max-w-xs hidden" onChange={(inputEvent) => { onFileInputChange(inputEvent, "logo") }}/>
                    <div className="shrink-0 pt-2"><EditButton onEditClick={() => {
                        document.getElementById('upload-add-map-logo-photo-input').click();
                    }} /></div>
                </div>
                <div className="flex flex-col">
                    <div className="font-bold text-left text-lg pr-4">
                        Dark mode
                    </div>
                    {
                        currentMap.darkLogo && <LazyLoadImage 
                        className='max-w-[200px] max-h-[200px] object-contain rounded-md'
                        src={currentMap.darkLogo}
                        />
                    }

                    <input id="upload-add-map-dark-logo-photo-input" type="file" accept="image/png, image/jpeg" className="file-input file-input-bordered w-full max-w-xs hidden" onChange={(inputEvent) => { onFileInputChange(inputEvent, "darkLogo") }}/>
                    <div className="shrink-0 pt-2"><EditButton onEditClick={() => {
                        document.getElementById('upload-add-map-dark-logo-photo-input').click();
                    }} /></div>
                </div>
            </div>

            <div className="px-4 md:px-8 text-left text-md text-base-content/70 pt-8 pb-1">
                Map description
            </div>
            <div className="flex flex-col px-4 md:px-8">
                <div className="text-left text-lg line-clamp-3" style={{ whiteSpace: 'pre-line' }} dangerouslySetInnerHTML={{ __html: currentMap.description }}>
                </div>
                <div className="shrink-0 pt-2"><EditButton onEditClick={onEditDescriptionClick} /></div>
            </div>

            <div className="px-4 md:px-8 text-left text-md text-base-content/70 pt-8 pb-1">
                Map Theme Color
            </div>
            <div className="flex flex-col items-start px-4 md:px-8">
                <div className="font-bold text-left text-lg pr-4 flex flex-row items-center gap-2">
                    {
                        primaryColor && <div className="text-left text-lg w-10 h-10 rounded-md" style={{ backgroundColor: primaryColor ?? "#FDCF2B" }}></div>
                    }
                    <div className="text-left text-md">
                        {primaryColor ?? "Using default color"}
                    </div>
                </div>
                <div className="shrink-0 pt-2"><EditButton onEditClick={onEditPrimaryColorClick} /></div>
            </div>

            <div className="px-4 md:px-8 text-left text-md text-base-content/70 pt-8 pb-1">
                Default view
            </div>
            <div className="px-4 md:px-8 text-left text-sm text-base-content/50 pb-2">
                Determines when viewers visit your URL, whether they see the map or list view first.
            </div>
            <div className="flex flex-row items-center px-4 md:px-8">
                <div className="font-bold text-left text-lg pr-4">
                    {currentMap.defaultView}
                </div>
                <div className="shrink-0 "><EditButton onEditClick={onEditDefaultViewClick} /></div>
            </div>

            <div className="px-4 md:px-8 text-left text-md text-base-content/70 pt-8 pb-1">
                Show sort button
            </div>
            <div className="px-4 md:px-8 text-left text-sm text-base-content/50 pb-2">
                Enable this to allow viewers to sort the list by different criteria, including recommended, alphabetically, and distance.
            </div>
            <div className="flex flex-row items-center px-4 md:px-8">
                <div className="font-bold text-left text-lg pr-4">
                    {currentMap.isSortAllowed ? "Yes" : "No"}
                </div>
                <div className="shrink-0 "><EditButton onEditClick={onEditIsSortAllowedClick} /></div>
            </div>

            <div className="px-4 md:px-8 text-left text-md text-base-content/70 pt-8 pb-1">
                Delete map
            </div>
            <div className="flex flex-col px-4 md:px-8">
                <div className="shrink-0 pt-2"><DeleteButton onDeleteClick={onDeleteMapClick} /></div>
            </div>

            <div className='max-w-1/2 fixed bottom-4 right-4 bg-transparent text-right flex flex-row gap-2 items-center'>
            <a href={`/${viewingMap.slug}`} target="_blank">
            <button className="btn text-base btn-accent normal-case rounded-full" onClick={() => {  }}>
            <svg className="w-6 h-6 fill-accent-content" viewBox="0 0 52 52" enable-background="new 0 0 52 52" >
            <g>
                <path d="M51.8,25.1C47.1,15.6,37.3,9,26,9S4.9,15.6,0.2,25.1c-0.3,0.6-0.3,1.3,0,1.8C4.9,36.4,14.7,43,26,43
                    s21.1-6.6,25.8-16.1C52.1,26.3,52.1,25.7,51.8,25.1z M26,37c-6.1,0-11-4.9-11-11s4.9-11,11-11s11,4.9,11,11S32.1,37,26,37z"/>
                <path d="M26,19c-3.9,0-7,3.1-7,7s3.1,7,7,7s7-3.1,7-7S29.9,19,26,19z"/>
            </g>
            </svg> Preview Map
            </button>
            </a>

            </div>

            <div className="pb-64"></div>
            <CoorayFooter background='bg-base-200' />

            <EditorModal 
            isEditorModalOpen={isEditorModalOpen} 
            onEditorModalDismiss={onEditorModalDismiss} 
            onEditorSaveClick={(html, editingKey) => { onEditorSaveClick(html, editingKey) }} 
            editingKey={editingEditorKey}
            initialValue={initialEditorValue}
            />

            <EditInputModal 
            isEditInputModalOpen={isEditInputModalOpen} 
            onEditInputModalDismiss={onEditInputModalDismiss} 
            onEditInputSaveClick={(inputValue, editingKey) => { onEditInputSaveClick(inputValue, editingKey) }} 
            editingKey={editingInputKey} 
            initialValue={initialInputValue} 
            label={inputLabel} 
            placeholder={inputPlaceholder} 
            pretext={inputPretext}
            />

            {<dialog id="edit-slug-modal" className="modal">
                <div className="modal-box w-11/12 max-w-5xl flex flex-col gap-2">
                    <p className="text-lg">⚠️ Warning: Changing the map URL will break all existing links to the map.</p>
                    {editSlugWarningMessageParameters && <p className="text-base">⛔️ <span className="font-bold">zipnmap.io/{editSlugWarningMessageParameters.oldSlug}</span> will no longer work</p>}
                    {editSlugWarningMessageParameters && <p className="text-base">✅ <span className="font-bold">zipmap.io/{editSlugWarningMessageParameters.newSlug}</span> will be the new map URL</p>}
                    <div className="modal-action">
                        <button className="btn btn-success" onClick={() => { 
                            document.getElementById("edit-slug-modal").close()
                            editMapSlug(editSlugWarningMessageParameters.newSlug)
                        }}>Confirm</button>
                        <button className="btn btn-ghost" onClick={() => { 
                            document.getElementById("edit-slug-modal").close()
                        }}>Cancel</button>
                    </div>
                </div>
            </dialog>
            }

            {
                isDashboardLiveStatusModalOpen && <DashboardLiveStatusModal
                isDashboardLiveStatusModalOpen={isDashboardLiveStatusModalOpen}
                onDashboardLiveStatusSaveClick={(isLive) => { onDashboardLiveStatusSaveClick(isLive) }}
                onDashboardLiveStatusModalDismiss={() => { setIsDashboardLiveStatusModalOpen(false) }}
                initialStatus={currentMap.isLive}
                />
            }

{
                isDashboardSortStatusModalOpen && <DashboardSortStatusModal
                isDashboardSortStatusModalOpen={isDashboardSortStatusModalOpen}
                onDashboardSortStatusSaveClick={(isSortAllowed) => { onDashboardSortStatusSaveClick(isSortAllowed) }}
                onDashboardSortStatusModalDismiss={() => { setIsDashboardSortStatusModalOpen(false) }}
                initialStatus={currentMap.isSortAllowed}
                />
            }

            {
                isDashboardDefaultViewModalOpen && <DashboardDefaultViewModal
                isDashboardDefaultViewModalOpen={isDashboardDefaultViewModalOpen}
                onDashboardDefaultViewSaveClick={(viewType) => { onDashboardDefaultViewSaveClick(viewType) }}
                onDashboardDefaultViewModalDismiss={() => { setIsDashboardDefaultViewModalOpen(false) }}
                initialStatus={currentMap.defaultView}
                />
            }
            {
                isColorSelectorModalOpen && <ColorSelectorModal
                isColorSelectorModalOpen={isColorSelectorModalOpen}
                onColorSelectorModalDismiss={() => { setIsColorSelectorModalOpen(false) }}
                initialColor={primaryColor ?? "#FDCF2B"}
                onColorSaveClick={onColorSaveClick}
                />
            }
            <dialog id="pick-emoji-modal" className="modal">
                <div className="modal-box w-max">
                    <h3 className="font-bold text-lg pb-4">Pick Marker Emoji</h3>
                    <Picker data={data} onEmojiSelect={(emoji) => { onEmojiSelected(emoji.native) }} />

                    <div className="modal-action">
                    <button className="btn btn-circle btn-soft" onClick={
                            () => {
                                document.getElementById("pick-emoji-modal").close()
                            }
                            }>
                        <svg className="fill-base-content w-5 h-5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" clip-rule="evenodd" d="M19.207 6.207a1 1 0 0 0-1.414-1.414L12 10.586 6.207 4.793a1 1 0 0 0-1.414 1.414L10.586 12l-5.793 5.793a1 1 0 1 0 1.414 1.414L12 13.414l5.793 5.793a1 1 0 0 0 1.414-1.414L13.414 12l5.793-5.793z"/></svg>
                    </button>
                    </div>
                </div>
            </dialog>
            {
                toastMessage && <div className="toast z-10">
                <div className={`alert ${toastMessage.type == "success" ? "alert-success" : "alert-info"}`}>
                <span className="text-sm">{toastMessage.message}</span>
                </div>
            </div>
            }
            <EditRichTextModal
            isEditRichTextModalOpen={isEditRichTextModalOpen}
            onEditRichTextSaveClick={(html, editingKey) => { onEditRichTextSaveClick(html, editingKey) }}
            onEditRichTextModalDismiss={() => { setIsEditRichTextModalOpen(false) }}
            editingKey={editingRichTextKey}
            initialValue={initialRichTextValue}
            label={richTextLabel}
            enablement={
                new TiptapControlEnablement(
                    {
                        isHeading1Enabled: false,
                        isHeading2Enabled: false,
                        isBoldEnabled: false,
                        isItalicEnabled: true,
                        isStrikeEnabled: false,
                        isCodeEnabled: false,
                        isLinkEnabled: false,
                    }
                )
            }
            />
        </div>
    )
}
